/**
 * The Investor Portals view's Services band (#11635 Round 4 R19, Round 5
 * R1, R4, R10, R12; slice 1 Round 3): the morning check. Every service is a
 * card-like tile in a dense grid — favicon tile, name, state pill and one
 * metadata line — grouped so what needs a look comes first, with the window's
 * totals in the lede. Clicking a tile opens that service's page (Round 7,
 * R21), where the tiles stand down for the service's header: its host, its
 * state, the reason it is not working, and its facts (Round 8, R26).
 *
 * Round 17 (R63 to R66): the tiles are one of two views. The band opens on
 * a list, one table of one-line rows (what needs attention first, with no
 * group headings), each saying since
 * when a service has needed a look but never why (stacked blocks on a
 * phone), and a toggle picks the cards, remembered per browser.
 *
 * The rules are in `browser/portals.ts`; this file only draws them.
 */
import React, { useRef, useState } from "react";
import { Tooltip } from "@mui/material";
import {
  PORTAL_ACTIVITY_WINDOW_DAYS,
  type PortalService,
} from "@/components/FpFundBrain/Manage/Connections/InvestorPortalsRow";
import { ServiceFavicon } from "@/components/common/ServiceFavicon";
import { useIsPhoneViewport } from "@/hooks/useViewportTier";
import { LayoutButton, LayoutToggle } from "@/components/FpPages/RMS/design";
import { GlobeIcon, ListIcon, SquaresFourIcon } from "@phosphor-icons/react";
import { RowPlaceholders, Section } from "./HomeSections";
import { rowActivationProps } from "./rowActivation";
import {
  givenUpClause,
  groupServices,
  isSyncing,
  listRowSince,
  overviewLedeClauses,
  pickedServices,
  pillStateOf,
  readServicesView,
  relativeOf,
  serviceManagers,
  sortServices,
  tileMetaText,
  writeServicesView,
  type PillState,
  type ServiceGroup as ServiceGroupOf,
  type ServicesView,
} from "./browser/portals";

export interface PortalsOverviewProps {
  services: PortalService[];
  /** The Source picks in force: the overview lists those services alone. */
  sourceKeys: string[];
  /** A tile's click: open the service's page, by the service's name. */
  onPickService: (service: string) => void;
  /** Inside a service (Round 7, R22): the page's title block describes it
   *  (R46), so the band shows nothing for it once it is resolved. */
  inService?: boolean;
  /** The service the page is inside, as the page resolved it; null while
   *  the services load, or when they could not be read. */
  service?: PortalService | null;
  /** The services read failed with no answer cached. */
  error?: boolean;
  /** The services read is still out: placeholders, no counts, so the lede
   *  never says "No services" about an organization whose services are
   *  about to arrive. */
  loading?: boolean;
  /** The host's frame is too narrow for the list's table (the listing's
   *  own fold): the list draws stacked blocks, as it does on a phone. */
  compact?: boolean;
}

/** A service's favicon, the globe when it has none: the tiles' mark and,
 *  beside a service page's heading, the page's (Round 8, R27). */
export const Mark: React.FC<{ service: PortalService; size?: number }> = ({
  service,
  size = 16,
}) => (
  <ServiceFavicon
    host={service.icon_host}
    label={service.service}
    size={size}
    fallback={
      <GlobeIcon
        size={size}
        aria-hidden
        className="shrink-0 text-[var(--fp-ink-3)]"
      />
    }
  />
);

/** One state's colours (slice 1 Round 3, R12; Round 5, R17, R19): the
 *  heading and state-line dot and the service header's pill. A tile is
 *  white with the neutral hairline for every state (Round 16, R55, on
 *  `--fp-border-strong`): its
 *  state line's dot and label carry the state.
 *  Exhaustive over the pill states, so a new state is a compile error here. */
interface StateTint {
  label: string;
  dot: string;
  pill: string;
}

export const STATE_TINT: Record<PillState, StateTint> = {
  needs_attention: {
    label: "Needs attention",
    dot: "bg-red-500",
    pill: "bg-red-50 text-red-700 ring-red-200",
  },
  enrolling: {
    label: "Enrolling",
    dot: "bg-amber-500",
    pill: "bg-amber-50 text-amber-700 ring-amber-200",
  },
  connected: {
    label: "Synced",
    dot: "bg-green-500",
    pill: "bg-green-50 text-green-700 ring-green-200",
  },
  // A run going: the Synced tint; the tile's outline pulses (index.css).
  syncing: {
    label: "Syncing",
    dot: "bg-green-500",
    pill: "bg-green-50 text-green-700 ring-green-200",
  },
};

/** A state pill (Round 3, R11): the state's dot and its label in its tint,
 *  beside a service page's title (Round 10, R35; a tile names its state
 *  on its state line). */
export const Pill: React.FC<{ state: PillState }> = ({ state }) => {
  const tint = STATE_TINT[state];
  return (
    <span
      data-state={state}
      className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-1.5 py-px text-[11px] font-medium ring-1 ring-inset ${tint.pill}`}
    >
      <span
        aria-hidden
        className={`inline-block h-1.5 w-1.5 rounded-full ${tint.dot}`}
      />
      {tint.label}
    </span>
  );
};

/** Text on one line, in full in a tooltip on hover only when it was cut:
 *  read when the hover begins (the Round 3 pattern). */
const CutText: React.FC<{ text: string; className?: string }> = ({
  text,
  className = "",
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <Tooltip
      title={text}
      placement="top-start"
      open={open}
      onOpen={() => {
        const node = ref.current;
        setOpen(node !== null && node.scrollWidth > node.clientWidth);
      }}
      onClose={() => setOpen(false)}
    >
      <span ref={ref} className={`min-w-0 truncate ${className}`}>
        {text}
      </span>
    </Tooltip>
  );
};

/** A tile's name on one line (R36), in full in a tooltip when cut. */
const TileName: React.FC<{ name: string }> = ({ name }) => (
  <CutText
    text={name}
    className="flex-1 text-[13.5px] font-medium leading-[1.3] text-[var(--fp-ink)]"
  />
);

/** Every tile is one height (R36, amended), the one a one-line name gives:
 *  the name 13.5px × 1.3 = 17.55px, the state line 18px, the metadata line
 *  16px, two 4px gaps and 2 × 10px of padding: 79.55px, so 80px. */
const TILE_HEIGHT = "h-[80px]";

/** One service as production's tile (R1, R10; Round 5, R19; R36): the
 *  favicon beside the name on one line (truncated, in full in a tooltip),
 *  the state's dot and label, and one metadata line, truncated too,
 *  outlined in the state's colour. The reason a service is not working
 *  stays off the tile (slice 1, R7), its status line included (R54): the
 *  service's page states it. */
const Tile: React.FC<{
  service: PortalService;
  onPick: () => void;
}> = ({ service, onPick }) => {
  const pill = pillStateOf(service);
  const tint = STATE_TINT[pill];
  const status = (
    <span className="min-w-0 w-full truncate text-[12px] leading-[16px] text-[var(--fp-ink-3)] [font-variant-numeric:tabular-nums]">
      {/* The line truncates at the tile's edge (R36); a clause never breaks
          inside itself ("3 docs in 7d"). The clauses are fixed in number
          and order per state, so the index keys them. */}
      {tileMetaText(service).map((clause, index) => (
        <React.Fragment key={index}>
          {index > 0 && " · "}
          <span className="whitespace-nowrap">{clause}</span>
        </React.Fragment>
      ))}
    </span>
  );
  return (
    <li
      data-state={service.state}
      data-syncing={isSyncing(service) || undefined}
      className={`min-w-0 rounded-[10px] border border-[var(--fp-border-strong)] bg-[var(--fp-surface)] ${
        pill === "syncing" ? "fp-portal-syncing" : ""
      }`}
    >
      <button
        type="button"
        aria-label={service.service}
        onClick={onPick}
        className={`flex ${TILE_HEIGHT} w-full cursor-pointer flex-col gap-1 overflow-hidden rounded-[9px] px-3 py-2.5 text-left hover:bg-[var(--fp-hover-row)]`}
      >
        <span className="flex min-w-0 w-full items-start gap-2">
          <span className="mt-px shrink-0">
            <Mark service={service} />
          </span>
          <TileName name={service.service} />
        </span>
        <span
          data-state={pill}
          className="flex min-w-0 w-full shrink-0 items-center gap-1.5 text-[12.5px] leading-[18px] text-[var(--fp-ink-2)]"
        >
          <span
            aria-hidden
            className={`inline-block h-2 w-2 shrink-0 rounded-full ${tint.dot}`}
          />
          {tint.label}
        </span>
        {status}
      </button>
    </li>
  );
};

/** A group's heading (slice 1, R1): its state's dot, its label and how
 *  many it holds, over the cards. */
const GroupHeading: React.FC<{ group: ServiceGroupOf }> = ({ group }) => (
  <h3 className="m-0 flex items-baseline gap-2 px-4 text-[11px] font-medium uppercase tracking-[0.04em] text-[var(--fp-ink-3)]">
    <span
      aria-hidden
      data-state={group.state}
      className={`inline-block h-2 w-2 shrink-0 self-center rounded-full ${
        STATE_TINT[group.state].dot
      }`}
    />
    <span>{STATE_TINT[group.state].label}</span>
    <span className="font-mono [font-variant-numeric:tabular-nums]">
      {group.services.length}
    </span>
  </h3>
);

/** One state's tiles under its label and count (slice 1, R1): every one
 *  of them, always (R39). */
const ServiceGroup: React.FC<{
  group: ServiceGroupOf;
  onPickService: (service: string) => void;
}> = ({ group, onPickService }) => {
  const label = STATE_TINT[group.state].label;
  return (
    <div role="group" aria-label={label} className="flex flex-col gap-2">
      <GroupHeading group={group} />
      {/* Production's cells (Round 5, R19): a phone's width fits one tile
          and a wider frame as many 200px tiles as it holds; the grid
          decides, not the tier. */}
      <ul className="m-0 grid list-none gap-2.5 p-0 px-4 grid-cols-[repeat(auto-fill,minmax(min(200px,100%),1fr))]">
        {group.services.map((service) => (
          <Tile
            key={service.connection_id}
            service={service}
            onPick={() => onPickService(service.service)}
          />
        ))}
      </ul>
    </div>
  );
};

// --- Round 17: the list view ----------------------------------------------

const ROW_INTERACTION =
  "cursor-pointer outline-none hover:bg-[var(--fp-hover-row)] focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-[var(--fp-ink-3)]";

/** A state's dot and label, as a tile's state line draws it, then since
 *  when for a state that needs a look (R65, amended): one line, never the
 *  reason. */
const StateLine: React.FC<{ service: PortalService; since?: boolean }> = ({
  service,
  since = false,
}) => {
  const pill = pillStateOf(service);
  const tint = STATE_TINT[pill];
  const sinceText = since ? listRowSince(service) : null;
  // What a connected service's syncs gave up on (#13095 R-R1b), in the
  // list's status cell only: the tiles are unchanged.
  const givenUp = since ? givenUpClause(service) : null;
  return (
    <span
      data-state={pill}
      className="flex min-w-0 items-center gap-1.5 whitespace-nowrap text-[13px] text-[var(--fp-ink)]"
    >
      <span
        aria-hidden
        className={`inline-block h-2 w-2 shrink-0 rounded-full ${tint.dot}`}
      />
      <span className="shrink-0">{tint.label}</span>
      {sinceText !== null && (
        <span className="min-w-0 truncate text-[var(--fp-ink-3)] [font-variant-numeric:tabular-nums]">
          · {sinceText}
        </span>
      )}
      {givenUp !== null && (
        <span className="min-w-0 truncate text-[var(--fp-ink-3)] [font-variant-numeric:tabular-nums]">
          · {givenUp}
        </span>
      )}
    </span>
  );
};

/** The Service cell (R65, amended): the favicon beside the name, on one
 *  line; the host stays on the service's page. */
const ServiceCell: React.FC<{ service: PortalService }> = ({ service }) => (
  <span className="flex min-w-0 items-center gap-2">
    <span className="flex shrink-0">
      <Mark service={service} />
    </span>
    <CutText
      text={service.service}
      className="text-[13.5px] font-medium text-[var(--fp-ink)]"
    />
  </span>
);

const listTh =
  "whitespace-nowrap px-3 py-2 text-left text-[11px] font-medium uppercase tracking-[0.04em] text-[var(--fp-ink-3)] border-b border-[var(--fp-border)]";
// Every row is one line at the table row height (R65, amended): no cell
// wraps, every cell truncates.
const listTd =
  "h-[var(--fp-row-h)] overflow-hidden text-ellipsis whitespace-nowrap px-3 py-0 align-middle text-[13px] text-[var(--fp-ink)]";

/** One service as a list row (R65). */
const ServiceRow: React.FC<{
  service: PortalService;
  onPick: () => void;
}> = ({ service, onPick }) => {
  const managers = serviceManagers(service).map((card) => card.name);
  return (
    <tr
      data-state={service.state}
      data-syncing={isSyncing(service) || undefined}
      data-service={service.service}
      className={`border-b border-[var(--fp-border)] ${ROW_INTERACTION}`}
      {...rowActivationProps(onPick)}
    >
      <td className={listTd}>
        <ServiceCell service={service} />
      </td>
      <td className={listTd}>
        <StateLine service={service} since />
      </td>
      <td
        className={`${listTd} text-[var(--fp-ink-2)] [font-variant-numeric:tabular-nums]`}
      >
        {isSyncing(service)
          ? "Syncing now"
          : service.last_sync_at
            ? relativeOf(service.last_sync_at)
            : "Never"}
      </td>
      <td
        className={`${listTd} text-right text-[var(--fp-ink-2)] [font-variant-numeric:tabular-nums]`}
      >
        {service.state === "connected"
          ? service.recent_document_count.toLocaleString()
          : "—"}
      </td>
      <td className={`${listTd} text-[var(--fp-ink-2)]`}>
        {managers.length > 0 ? (
          <span className="flex min-w-0">
            <CutText text={managers.join(", ")} />
          </span>
        ) : (
          <span className="text-[var(--fp-ink-3)]">—</span>
        )}
      </td>
    </tr>
  );
};

/** The list on a desktop frame (R65, amended): one table, one body, no
 *  group rows; the services in the groups' order flattened, so what needs
 *  attention comes first. */
const ServiceTable: React.FC<{
  services: PortalService[];
  onPickService: (service: string) => void;
}> = ({ services, onPickService }) => (
  <div className="px-4">
    <table className="w-full table-fixed border-collapse">
      <thead>
        <tr>
          <th scope="col" className={`${listTh} w-[26%]`}>
            Service
          </th>
          <th scope="col" className={`${listTh} w-[28%]`}>
            Status
          </th>
          <th scope="col" className={`${listTh} w-[12%]`}>
            Last sync
          </th>
          <th scope="col" className={`${listTh} w-[10%] text-right`}>
            Last {PORTAL_ACTIVITY_WINDOW_DAYS} days
          </th>
          <th scope="col" className={listTh}>
            Managers
          </th>
        </tr>
      </thead>
      <tbody>
        {services.map((service) => (
          <ServiceRow
            key={service.connection_id}
            service={service}
            onPick={() => onPickService(service.service)}
          />
        ))}
      </tbody>
    </table>
  </div>
);

/** One service as a phone block (R66): the favicon and name with the
 *  state at the right, then the facts a tile shows (never the reason). */
const ServiceBlock: React.FC<{
  service: PortalService;
  onPick: () => void;
}> = ({ service, onPick }) => (
  <li
    data-state={service.state}
    data-syncing={isSyncing(service) || undefined}
    data-service={service.service}
    className={`flex flex-col gap-1 border-b border-[var(--fp-border)] px-4 py-2.5 first:border-t ${ROW_INTERACTION}`}
    {...rowActivationProps(onPick)}
  >
    <span className="flex min-w-0 items-center gap-3">
      <span className="min-w-0 flex-1">
        <ServiceCell service={service} />
      </span>
      <span className="shrink-0">
        <StateLine service={service} />
      </span>
    </span>
    <span className="text-[12px] text-[var(--fp-ink-3)] [font-variant-numeric:tabular-nums]">
      {/* A clause never breaks inside itself ("3 docs in 7d"), as on the
          tile; the clauses are fixed in number and order per state, so
          the index keys them. */}
      {blockMetaText(service).map((clause, index) => (
        <React.Fragment key={index}>
          {index > 0 && " · "}
          <span className="whitespace-nowrap">{clause}</span>
        </React.Fragment>
      ))}
    </span>
  </li>
);

/** A phone block's metadata line: the tile's clauses, then what a
 *  connected service's syncs gave up on (#13095 R-R1b), as the desktop
 *  list's status cell says it. */
const blockMetaText = (service: PortalService): string[] => {
  const givenUp = givenUpClause(service);
  return [...tileMetaText(service), ...(givenUp === null ? [] : [givenUp])];
};

/** The list on a phone (R66, amended): the services as stacked blocks in
 *  the table's order, with no group headings. */
const ServiceBlocks: React.FC<{
  services: PortalService[];
  onPickService: (service: string) => void;
}> = ({ services, onPickService }) => (
  <ul className="m-0 list-none p-0">
    {services.map((service) => (
      <ServiceBlock
        key={service.connection_id}
        service={service}
        onPick={() => onPickService(service.service)}
      />
    ))}
  </ul>
);

const PortalsOverview: React.FC<PortalsOverviewProps> = ({
  services,
  sourceKeys,
  onPickService,
  loading = false,
  inService = false,
  service = null,
  error = false,
  compact = false,
}) => {
  const phone = useIsPhoneViewport();
  // The view this browser last picked (R63), read once on mount and
  // written on every change.
  const [view, setView] = useState<ServicesView>(readServicesView);
  const listed = sortServices(pickedServices(services, sourceKeys));
  const ledeClauses = overviewLedeClauses(listed);
  const pick = (next: ServicesView) => {
    setView(next);
    writeServicesView(next);
  };
  // The Agents page's icon pair (R63, amended), List on the left and Cards
  // on the right here: the list is this band's default.
  const toggle = (
    <LayoutToggle label="Services view">
      <LayoutButton
        label="List"
        active={view === "list"}
        onClick={() => pick("list")}
      >
        <ListIcon size={16} />
      </LayoutButton>
      <LayoutButton
        label="Cards"
        active={view === "cards"}
        onClick={() => pick("cards")}
      >
        <SquaresFourIcon size={16} />
      </LayoutButton>
    </LayoutToggle>
  );

  // A resolved service is described in the page's title block (R46): the
  // band starts with its Managers.
  if (inService && service !== null) return null;
  if (error) {
    const failed = (
      <p className="m-0 px-4 text-[13px] text-red-600">
        Could not load the services
      </p>
    );
    return inService ? (
      <section aria-label="Service" className="flex flex-col">
        {failed}
      </section>
    ) : (
      <Section title="Services" end={toggle}>
        {failed}
      </Section>
    );
  }
  if (loading) {
    // Inside a service the page is one service while the read is out, so
    // its placeholders hold the header's place, not the tiles'.
    return inService ? (
      <section aria-label="Service" aria-busy="true">
        <RowPlaceholders />
      </section>
    ) : (
      <Section title="Services" end={toggle}>
        <RowPlaceholders />
      </Section>
    );
  }
  const groups = groupServices(listed);
  const body = ((): React.ReactNode => {
    switch (view) {
      case "cards":
        return groups.map((group) => (
          <ServiceGroup
            key={group.state}
            group={group}
            onPickService={onPickService}
          />
        ));
      case "list": {
        // One run in the groups' order, with no headings (R65, amended):
        // what needs attention first, then enrolling, then connected.
        const ordered = groups.flatMap((group) => group.services);
        // Below the tier line, or in a frame too narrow for the table, the
        // list is stacked blocks (R66).
        return phone || compact ? (
          <ServiceBlocks services={ordered} onPickService={onPickService} />
        ) : (
          <ServiceTable services={ordered} onPickService={onPickService} />
        );
      }
      default: {
        const unhandled: never = view;
        throw new Error(`unhandled services view: ${String(unhandled)}`);
      }
    }
  })();
  return (
    <Section
      title="Services"
      // The window's totals beside the heading, in its count style (R63,
      // amended): what arrived, what went quiet, what is broken — the
      // whole morning check on the heading's own line.
      trailing={
        <span
          data-testid="services-lede"
          className="text-[12.5px] font-normal text-[var(--fp-ink-3)]"
        >
          {/* A clause never breaks inside itself on a narrow frame, as on
              the tile's metadata line; the clauses are fixed in order, so
              the index keys them. */}
          {ledeClauses.map((clause, index) => (
            <React.Fragment key={index}>
              {index > 0 && " · "}
              <span className="whitespace-nowrap">{clause}</span>
            </React.Fragment>
          ))}
        </span>
      }
      end={toggle}
    >
      {body}
    </Section>
  );
};

export default PortalsOverview;
